import React, { useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  ButtonGroup,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Switch,
  Typography,
} from '@material-ui/core';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, Resolver, useForm, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useHistory, useRouteMatch } from 'react-router-dom';
import * as yup from 'yup';
import { mealPlanApi, patientApi } from '../api';
import { TdeeCalculateRequestActivityLevelEnum } from '../api/generated/models';
import { LoadingSpinner } from '../components/loadingSpinner';
import { MuiCustomSelect, MuiCustomTextField } from '../components/muiCustomInput';
import { useStore } from '../context';
import { Trans, useTranslation } from '../i18n';
import './patient-meal-plan-intake.scss';

interface ActiveSlots {
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
  snacks: boolean;
}

type WeightGoal = 'maintain' | 'lose' | 'gain';

interface IntakeFormValues {
  age: number | '';
  sex: string;
  weight_kg: number | '';
  height_cm: number | '';
  weight_lb: number | '';
  height_ft: number | '';
  height_in: number | '';
  activity_level: string;
  title: string;
  num_days: number;
  weight_goal: WeightGoal;
  target_calories: number | '';
  target_protein_g: number | '';
  target_carbs_g: number | '';
  target_fat_g: number | '';
  diet_labels: string[];
  health_labels: string[];
  exclusions: string;
  active_slots: ActiveSlots;
  family_size: number;
  motivations: string;
  recent_meal_context_text: string;
}

/* eslint-disable i18next/no-literal-string */
const DIET_LABELS = ['balanced', 'high-protein', 'low-carb', 'low-fat', 'high-fiber'];
const HEALTH_LABELS = [
  'gluten-free',
  'dairy-free',
  'vegan',
  'vegetarian',
  'peanut-free',
  'tree-nut-free',
  'soy-free',
  'egg-free',
  'fish-free',
  'shellfish-free',
  'pork-free',
  'kosher',
  'halal',
];

const ACTIVITY_LEVELS = [
  { value: TdeeCalculateRequestActivityLevelEnum.Sedentary, label: 'Sedentary (little or no exercise)' },
  { value: TdeeCalculateRequestActivityLevelEnum.LightlyActive, label: 'Lightly active (1-3 days/week)' },
  { value: TdeeCalculateRequestActivityLevelEnum.ModeratelyActive, label: 'Moderately active (3-5 days/week)' },
  { value: TdeeCalculateRequestActivityLevelEnum.VeryActive, label: 'Very active (6-7 days/week)' },
  { value: TdeeCalculateRequestActivityLevelEnum.ExtraActive, label: 'Extra active (very intense exercise)' },
];

const WEIGHT_GOAL_OPTIONS: { value: WeightGoal, label: string, multiplier: number }[] = [
  { value: 'maintain', label: 'Maintain weight', multiplier: 1 },
  { value: 'lose', label: 'Lose weight', multiplier: 0.8 },
  { value: 'gain', label: 'Gain weight', multiplier: 1.15 },
];
/* eslint-enable i18next/no-literal-string */

const parseRecentMealContextToLines = (items?: string[] | null): string => {
  if (!items?.length) {
    return '';
  }
  const names = items.map((item) => {
    try {
      const parsed = JSON.parse(item) as { name?: unknown };
      return typeof parsed?.name === 'string' ? parsed.name : '';
    } catch {
      return '';
    }
  }).filter(Boolean);
  // Originally when the recent meal context was backend only, duplicates were allowed
  // Now, since this is exposed to users, food names are deduplicated to avoid confusion and improve UX
  return Array.from(new Set(names)).join('\n');
};

const linesToRecentMealContextJsonRows = (text: string): string[] => {
  const lines = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (lines.length === 0) {
    return [];
  }
  return lines.map((name) => JSON.stringify({ name }));
};

const KG_TO_LB = 2.205;
const FT_TO_CM = 30.48;
const IN_TO_CM = 2.54;
const IN_TO_FT = 12;

const roundToOneDecimalPlace = (value: number) => {
  return Math.round(value * 10) / 10;
};

const convertKgToLb = (kg: number) => {
  return roundToOneDecimalPlace(kg * KG_TO_LB);
};

const convertLbToKg = (lb: number) => {
  return roundToOneDecimalPlace(lb / KG_TO_LB);
};

const convertFtToCm = (ft: number, inch: number) => {
  return roundToOneDecimalPlace(ft * FT_TO_CM + inch * IN_TO_CM);
};

const convertCmToFtIn = (cm: number) => {
  const totalInches = cm / IN_TO_CM;
  const ft = Math.floor(totalInches / IN_TO_FT);
  const inches = roundToOneDecimalPlace(totalInches - ft * IN_TO_FT);
  return { ft, inches };
};

const convertImperialToMetricFields = (
  getValues: UseFormGetValues<IntakeFormValues>,
  setValue: UseFormSetValue<IntakeFormValues>,
) => {
  const weight_lb = Number(getValues('weight_lb'));
  const height_ft = Number(getValues('height_ft')) || 0;
  const height_in = Number(getValues('height_in')) || 0;
  if (weight_lb > 0) {
    setValue('weight_kg', convertLbToKg(weight_lb));
  }
  if (height_ft > 0 || height_in > 0) {
    const cm = convertFtToCm(height_ft, height_in);
    setValue('height_cm', roundToOneDecimalPlace(cm));
  }
  setValue('weight_lb', '');
  setValue('height_ft', '');
  setValue('height_in', '');
};

const convertMetricToImperialFields = (
  getValues: UseFormGetValues<IntakeFormValues>,
  setValue: UseFormSetValue<IntakeFormValues>,
) => {
  const weight_kg = Number(getValues('weight_kg'));
  const height_cm = Number(getValues('height_cm'));
  if (weight_kg > 0) {
    setValue('weight_lb', convertKgToLb(weight_kg));
  }
  if (height_cm > 0) {
    const { ft, inches } = convertCmToFtIn(height_cm);
    setValue('height_ft', ft);
    setValue('height_in', inches);
  }
  setValue('weight_kg', '');
  setValue('height_cm', '');
};

const NUM_DAYS_OPTIONS = [3, 5, 7];

const adjustedCaloriesFromTdee = (tdee: number, goal: WeightGoal): number => {
  const mult = WEIGHT_GOAL_OPTIONS.find((o) => o.value === goal)?.multiplier ?? 1;
  return tdee * mult;
};

const macrosFromCalories = (calories: number) => ({
  protein_g: Math.round((calories * 0.25) / 4),
  carbs_g: Math.round((calories * 0.45) / 4),
  fat_g: Math.round((calories * 0.30) / 9),
});

/* eslint-disable i18next/no-literal-string */
const mapPatientSexToTdeeSex = (patientSex: string | undefined): string => {
  if (!patientSex) {
    return '';
  }
  if (patientSex === 'male' || patientSex === 'ftm_male') {
    return 'male';
  }
  if (patientSex === 'female' || patientSex === 'mtf_female') {
    return 'female';
  }
  return '';
};

const buildValidationSchema = (t: (key: string) => string, imperial: boolean) =>
  yup.object({
    age: yup.number().typeError(t('Age is required')).required(t('Age is required')).min(1).max(120),
    sex: yup.string().required(t('Sex is required')).oneOf(['male', 'female']),
    weight_kg: imperial
      ? yup.number().transform((v, orig) => orig === '' ? undefined : v).notRequired()
      : yup.number().typeError(t('Weight is required')).required(t('Weight is required')).min(1).max(500),
    height_cm: imperial
      ? yup.number().transform((v, orig) => orig === '' ? undefined : v).notRequired()
      : yup.number().typeError(t('Height is required')).required(t('Height is required')).min(50).max(300),
    weight_lb: imperial
      ? yup.number().typeError(t('Weight is required')).required(t('Weight is required')).min(1).max(1100)
      : yup.number().transform((v, orig) => orig === '' ? undefined : v).notRequired(),
    height_ft: imperial
      ? yup.number().typeError(t('Feet is required')).required(t('Feet is required')).min(0).max(8)
      : yup.number().transform((v, orig) => orig === '' ? undefined : v).notRequired(),
    height_in: imperial
      ? yup.number().typeError(t('Inches is required')).required(t('Inches is required')).min(0).max(11)
      : yup.number().transform((v, orig) => orig === '' ? undefined : v).notRequired(),
    activity_level: yup.string().required(t('Activity level is required')),
    title: yup.string().required(t('Title is required')),
    num_days: yup.number().required(),
    weight_goal: yup.string().required().oneOf(['maintain', 'lose', 'gain']),
    target_calories: yup.number().typeError(t('Calories are required')).required(t('Calories are required')).min(500)
      .max(10000),
    target_protein_g: yup.number().typeError(t('Required')).required(t('Required')).min(0),
    target_carbs_g: yup.number().typeError(t('Required')).required(t('Required')).min(0),
    target_fat_g: yup.number().typeError(t('Required')).required(t('Required')).min(0),
    diet_labels: yup.array().of(yup.string()),
    health_labels: yup.array().of(yup.string()),
    exclusions: yup.string(),
    active_slots: yup.object({
      breakfast: yup.boolean().required(),
      lunch: yup.boolean().required(),
      dinner: yup.boolean().required(),
      snacks: yup.boolean().required(),
    }),
    family_size: yup.number().required().min(1, t('Must be at least 1')).max(12, t('Must be at most 12')),
    motivations: yup.string(),
    recent_meal_context_text: yup.string(),
  });

const getMacroWarnings = (calories: number, protein_g: number, carbs_g: number, fat_g: number) => {
  const warnings: string[] = [];
  if (!calories || calories <= 0) {
    return warnings;
  }

  const proteinCals = protein_g * 4;
  const fatCals = fat_g * 9;

  if (proteinCals / calories > 0.4) {
    warnings.push('Protein exceeds 40% of total calories');
  }
  if (fatCals / calories > 0.45) {
    warnings.push('Fat exceeds 45% of total calories');
  }

  const totalMacroCals = proteinCals + (carbs_g * 4) + fatCals;
  const diff = Math.abs(totalMacroCals - calories);
  if (diff > calories * 0.1) {
    warnings.push('Macro totals differ from calorie target by more than 10%');
  }

  return warnings;
};

export const MealPlanIntakeForm = () => {
  const { patient, clinician } = useStore();
  const { t, tAttr } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const queryClient = useQueryClient();

  const clinicianDefaultImperial = useMemo(() => {
    const clinics = clinician?.clinics || [];
    return clinics.some((c) => c.country === 'USA' || c.country === 'US');
  }, [clinician]);

  const [useImperial, setUseImperial] = useState<boolean>(clinicianDefaultImperial);

  const [tdeeResult, setTdeeResult] = useState<{ bmr: number, tdee: number } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const prefillAppliedForPatientIdRef = useRef<number | null>(null);

  const patientQuery = useQuery(
    ['patient-with-recent-meal-context', patient?.patient_id],
    async () => {
      const res = await patientApi.appApiPatientGetPatient({
        patient_id: patient.patient_id,
        include_recent_meal_context: true,
      });
      return res.data;
    },
    { enabled: !!patient?.patient_id },
  );

  const patientData = patientQuery.data;

  // Fetch existing meal plans to pre-fill from most recent intake_form_data
  const mealPlansQuery = useQuery(
    ['meal-plans', patient?.patient_id],
    async () => {
      const res = await mealPlanApi.appApiMealPlanGetMealPlans({
        patient_id: patient.patient_id,
      });
      return res.data;
    },
    { enabled: !!patient?.patient_id },
  );

  const lastIntakeData = useMemo(() => {
    const plans = mealPlansQuery.data as any[] | undefined;
    if (!plans || plans.length === 0) {
      return null;
    }
    const withIntake = plans.filter((p) => p.intake_form_data);
    if (withIntake.length === 0) {
      return null;
    }
    return withIntake[0].intake_form_data as Record<string, any>;
  }, [mealPlansQuery.data]);

  const calculatedAge = useMemo(() => {
    if (!patientData?.birthday) {
      return '';
    }
    return Math.floor(DateTime.now().diff(DateTime.fromISO(patientData.birthday), 'years').years);
  }, [patientData?.birthday]);

  const schema = useMemo(() => buildValidationSchema(t, useImperial), [t, useImperial]);
  const form = useForm<IntakeFormValues>({
    reValidateMode: 'onChange',
    defaultValues: {
      age: '',
      sex: '',
      weight_kg: '',
      height_cm: '',
      weight_lb: '',
      height_ft: '',
      height_in: '',
      activity_level: '',
      title: t('7-Day Meal Plan'),
      num_days: 7,
      weight_goal: 'maintain',
      target_calories: '',
      target_protein_g: '',
      target_carbs_g: '',
      target_fat_g: '',
      diet_labels: [],
      health_labels: [],
      exclusions: '',
      active_slots: { breakfast: true, lunch: true, dinner: true, snacks: true },
      family_size: 1,
      motivations: '',
      recent_meal_context_text: '',
    },
    resolver: yupResolver(schema) as Resolver<any>,
  });

  const { control, handleSubmit, watch, setValue, getValues, trigger, formState: { errors } } = form;

  const handleSelectMetricUnits = () => {
    if (useImperial) {
      convertImperialToMetricFields(getValues, setValue);
    }
    setUseImperial(false);
  };

  const handleSelectImperialUnits = () => {
    if (!useImperial) {
      convertMetricToImperialFields(getValues, setValue);
    }
    setUseImperial(true);
  };

  // Pre-fill from patient profile and most recent meal plan intake data
  useEffect(() => {
    if (!patientData) {
      return;
    }

    // Avoid re-running prefill on query refetches (e.g. window focus) that would replace clinician edits
    if (prefillAppliedForPatientIdRef.current === patientData.id) {
      return;
    }

    if (calculatedAge) {
      setValue('age', calculatedAge);
    }
    const mappedSex = mapPatientSexToTdeeSex(patientData.sex);
    if (mappedSex) {
      setValue('sex', mappedSex);
    }

    // This serves as a fallback in case the patient has no prior meal plans or if the prior meal plans had no motivations
    // Later in the useEffect, motivations will be replaced with the values from the most recent meal plan if it exists
    if (patientData.motivations && patientData.motivations.length) {
      const motivationsStr = patientData.motivations.map((m) => tAttr(m, 'name')).join(', ');
      setValue('motivations', motivationsStr);
    }

    if (lastIntakeData) {
      if (lastIntakeData.weight_kg) {
        setValue('weight_kg', lastIntakeData.weight_kg);
      }
      if (lastIntakeData.height_cm) {
        setValue('height_cm', lastIntakeData.height_cm);
      }
      if (lastIntakeData.activity_level) {
        setValue('activity_level', lastIntakeData.activity_level);
      }
      if (lastIntakeData.weight_goal) {
        setValue('weight_goal', lastIntakeData.weight_goal);
      }
    }

    // Pre-fill new fields from most recent meal plan
    const plans = mealPlansQuery.data as any[] | undefined;
    const lastPlan = plans && plans.length > 0 ? plans[0] : null;
    if (lastPlan) {
      if (lastPlan.active_slots) {
        setValue('active_slots', lastPlan.active_slots);
      }
      if (lastPlan.family_size) {
        setValue('family_size', lastPlan.family_size);
      }
      if (lastPlan.motivations) {
        setValue('motivations', lastPlan.motivations);
      }
    }

    const contextFromPatientData = parseRecentMealContextToLines(patientData.recent_meal_context);
    if (contextFromPatientData) {
      setValue('recent_meal_context_text', contextFromPatientData);
    }

    prefillAppliedForPatientIdRef.current = patientData.id;
  }, [patientData, calculatedAge, lastIntakeData, mealPlansQuery.data, setValue, tAttr]);

  const [
    age,
    sex,
    weight_kg,
    height_cm,
    weight_lb,
    height_ft,
    height_in,
    activity_level,
    weight_goal,
    watchedCalories,
    watchedProtein,
    watchedCarbs,
    watchedFat,
    numDays,
  ] = watch([
    'age',
    'sex',
    'weight_kg',
    'height_cm',
    'weight_lb',
    'height_ft',
    'height_in',
    'activity_level',
    'weight_goal',
    'target_calories',
    'target_protein_g',
    'target_carbs_g',
    'target_fat_g',
    'num_days',
  ]);

  // Keep title in sync with num_days
  useEffect(() => {
    if (numDays) {
      setValue('title', t('{{numDays}}-Day Meal Plan', { numDays }));
    }
  }, [numDays, setValue, t]);

  const canCalculateTdee = useImperial
    ? !!age && !!sex && !!weight_lb && (!!height_ft || !!height_in) && !!activity_level
    : !!age && !!sex && !!weight_kg && !!height_cm && !!activity_level;

  const basePath = match.url.replace(/\/new$/, '');

  const tdeeMutation = useMutation(async () => {
    const request: any = {
      age_years: Number(age),
      sex: sex as 'male' | 'female',
      activity_level: activity_level as typeof TdeeCalculateRequestActivityLevelEnum[
        keyof typeof TdeeCalculateRequestActivityLevelEnum
      ],
    };
    if (useImperial) {
      request.weight_kg = convertLbToKg(Number(weight_lb));
      request.height_cm = convertFtToCm(Number(height_ft) || 0, Number(height_in) || 0);
    } else {
      request.weight_kg = Number(weight_kg);
      request.height_cm = Number(height_cm);
    }
    request.unit_system = 'metric';
    const res = await mealPlanApi.appApiMealPlanPostTdeeCalculate({
      patient_id: patient.patient_id,
      TdeeCalculateRequest: request,
    });
    return res.data;
  }, {
    onSuccess: (data) => {
      setTdeeResult({ bmr: data.bmr_calories, tdee: data.tdee_calories });
    },
    onError: (err) => {
      Sentry.captureException(err);
    },
  });

  // Auto-trigger TDEE calculation when all inputs are filled (debounced).
  useEffect(() => {
    const ageNum = Number(age);
    if (!(ageNum > 0 && sex && activity_level)) {
      return;
    }
    if (useImperial) {
      const weightNum = Number(weight_lb);
      const ftNum = Number(height_ft) || 0;
      const inNum = Number(height_in) || 0;
      if (!(weightNum > 0 && (ftNum > 0 || inNum > 0))) {
        return;
      }
    } else {
      const weightNum = Number(weight_kg);
      const heightNum = Number(height_cm);
      if (!(weightNum > 0 && heightNum > 0)) {
        return;
      }
    }
    const timer = setTimeout(() => {
      tdeeMutation.mutate();
    }, 600);
    return () => clearTimeout(timer);
  }, [age, sex, weight_kg, height_cm, weight_lb, height_ft, height_in, activity_level, useImperial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tdee = tdeeResult?.tdee;
    if (tdee == null || !Number.isFinite(tdee) || tdee <= 0) {
      return;
    }
    const goal = weight_goal;
    const adjusted = Math.round(adjustedCaloriesFromTdee(tdee, goal));
    setValue('target_calories', adjusted);
    const m = macrosFromCalories(adjusted);
    setValue('target_protein_g', m.protein_g);
    setValue('target_carbs_g', m.carbs_g);
    setValue('target_fat_g', m.fat_g);
  }, [tdeeResult?.tdee, weight_goal, setValue]);

  const macroWarnings = getMacroWarnings(
    Number(watchedCalories) || 0,
    Number(watchedProtein) || 0,
    Number(watchedCarbs) || 0,
    Number(watchedFat) || 0,
  );

  const createMutation = useMutation(async (values: IntakeFormValues) => {
    const exclusionsArray = values.exclusions
      ? values.exclusions.split(',').map((s) => s.trim()).filter(Boolean)
      : [];

    const recentMealContextRows = linesToRecentMealContextJsonRows(values.recent_meal_context_text);

    const res = await mealPlanApi.appApiMealPlanPostMealPlan({
      patient_id: patient.patient_id,
      CreateMealPlanRequest: {
        title: values.title,
        num_days: values.num_days,
        target_calories: Number(values.target_calories),
        target_protein_g: Number(values.target_protein_g),
        target_carbs_g: Number(values.target_carbs_g),
        target_fat_g: Number(values.target_fat_g),
        diet_labels: values.diet_labels,
        health_labels: values.health_labels,
        exclusions: exclusionsArray.length > 0 ? exclusionsArray : undefined,
        active_slots: values.active_slots,
        family_size: values.family_size,
        motivations: values.motivations || undefined,
        intake_form_data: {
          age_years: Number(values.age),
          sex: values.sex,
          activity_level: values.activity_level,
          weight_goal: values.weight_goal,
          bmr_calories: tdeeResult?.bmr,
          tdee_calories: tdeeResult?.tdee,
          recent_meal_context: recentMealContextRows,
          ...(useImperial
            ? {
              unit_system: 'imperial',
              weight_lb: Number(values.weight_lb),
              height_ft: Number(values.height_ft),
              height_in: Number(values.height_in),
            }
            : { unit_system: 'metric', weight_kg: Number(values.weight_kg), height_cm: Number(values.height_cm) }),
        },
      },
    });
    return res.data;
  }, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(['meal-plans', patient.patient_id]);
      history.push(`${basePath}/${data.id}`);
    },
    onError: (err: any) => {
      Sentry.captureException(err);
      setSubmitError(err?.response?.data?.detail || err?.message || t('Failed to create meal plan'));
    },
  });

  const onSubmit = (values: IntakeFormValues) => {
    setSubmitError(null);
    createMutation.mutate(values);
  };

  const handleBack = () => {
    history.push(basePath);
  };

  if (patientQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary
      fallbackRender={() => (
        <div>
          <Trans>Error loading intake form</Trans>
        </div>
      )}
      onError={(error) => Sentry.captureException(error)}
    >
      <Grid container style={{ marginTop: 30, marginBottom: 30 }}>
        <Grid item md={10} lg={8}>
          <div className="mealPlanIntake">
            <div className="intakeHeader">
              <Button onClick={handleBack} className="backBtn">
                &lt; <Trans>Back to Meal Plans</Trans>
              </Button>
              <h2>
                <Trans>Create Meal Plan</Trans>
              </h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Patient Info Section */}
              <section className="formSection">
                <Typography variant="h6" className="sectionTitle">
                  <Trans>Patient Information</Trans>
                </Typography>
                <div>
                  <Typography variant="body2" color="textSecondary" component="span" className="unitToggleLabel">
                    <Trans>Units of Measurement</Trans>
                  </Typography>
                  <ButtonGroup size="small" color="primary" style={{ marginBottom: 16, marginLeft: 16 }}>
                    <Button
                      variant={!useImperial ? 'contained' : 'outlined'}
                      onClick={handleSelectMetricUnits}
                    >
                      <Trans>Metric</Trans>
                    </Button>
                    <Button
                      variant={useImperial ? 'contained' : 'outlined'}
                      onClick={handleSelectImperialUnits}
                    >
                      <Trans>Imperial</Trans>
                    </Button>
                  </ButtonGroup>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="age"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Age')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.age}
                          helperText={errors.age?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="sex"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" size="small" fullWidth error={!!errors.sex}>
                          <InputLabel>{t('Sex')}</InputLabel>
                          <MuiCustomSelect {...field} label={t('Sex')}>
                            <MenuItem value="male">{t('Male')}</MenuItem>
                            <MenuItem value="female">{t('Female')}</MenuItem>
                          </MuiCustomSelect>
                          {errors.sex && <FormHelperText>{errors.sex.message as string}</FormHelperText>}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2} style={useImperial ? undefined : { display: 'none' }}>
                    <Controller
                      name="weight_lb"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Weight (lbs)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.weight_lb}
                          helperText={errors.weight_lb?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3} sm={2} style={useImperial ? undefined : { display: 'none' }}>
                    <Controller
                      name="height_ft"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Height (ft)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.height_ft}
                          helperText={errors.height_ft?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={3} sm={2} style={useImperial ? undefined : { display: 'none' }}>
                    <Controller
                      name="height_in"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Height (in)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.height_in}
                          helperText={errors.height_in?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} style={!useImperial ? undefined : { display: 'none' }}>
                    <Controller
                      name="weight_kg"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Weight (kg)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.weight_kg}
                          helperText={errors.weight_kg?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} style={!useImperial ? undefined : { display: 'none' }}>
                    <Controller
                      name="height_cm"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Height (cm)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.height_cm}
                          helperText={errors.height_cm?.message as string}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <div style={{ marginTop: 16 }}>
                  <Controller
                    name="motivations"
                    control={control}
                    render={({ field }) => (
                      <MuiCustomTextField
                        {...field}
                        label={t('Health Goals & Motivations')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={t('e.g. Type 2 diabetic, wants to reduce A1C, prefers Mediterranean-style meals')}
                      />
                    )}
                  />
                </div>
              </section>

              {/* Activity Level */}
              <section className="formSection">
                <Typography variant="h6" className="sectionTitle">
                  <Trans>Activity Level</Trans>
                </Typography>
                <Controller
                  name="activity_level"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="outlined" size="small" fullWidth error={!!errors.activity_level}>
                      <InputLabel>{t('Activity Level')}</InputLabel>
                      <MuiCustomSelect {...field} label={t('Activity Level')}>
                        {ACTIVITY_LEVELS.map((level) => (
                          <MenuItem key={level.value} value={level.value}>
                            {t(level.label)}
                          </MenuItem>
                        ))}
                      </MuiCustomSelect>
                      {errors.activity_level && (
                        <FormHelperText>{errors.activity_level.message as string}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </section>

              {/* TDEE Result Panel */}
              <section className="formSection tdeePanel">
                <Typography variant="h6" className="sectionTitle">
                  <Trans>TDEE Calculation</Trans>
                </Typography>
                {!canCalculateTdee && (
                  <Typography variant="caption" color="textSecondary">
                    <Trans>Fill in age, sex, weight, height, and activity level to calculate</Trans>
                  </Typography>
                )}
                {tdeeMutation.isLoading && (
                  <Typography variant="body2" color="textSecondary">
                    <Trans>Calculating...</Trans>
                  </Typography>
                )}
                {tdeeMutation.isError && (
                  <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                    <Trans>Failed to calculate TDEE. Please check inputs and try again.</Trans>
                  </Typography>
                )}
                {tdeeResult && (
                  <div className="tdeeResults">
                    <div className="tdeeValue">
                      <span className="tdeeLabel">{t('BMR')}</span>
                      <span className="tdeeNumber">{Math.round(tdeeResult.bmr)} {t('kcal')}</span>
                    </div>
                    <div className="tdeeValue">
                      <span className="tdeeLabel">{t('TDEE')}</span>
                      <span className="tdeeNumber">{Math.round(tdeeResult.tdee)} {t('kcal')}</span>
                    </div>
                  </div>
                )}
                {tdeeResult && (
                  <div style={{ marginTop: 16, maxWidth: 420 }}>
                    <Controller
                      name="weight_goal"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={!tdeeResult}
                          error={!!errors.weight_goal}
                        >
                          <InputLabel>{t('Weight goal')}</InputLabel>
                          <MuiCustomSelect {...field} label={t('Weight goal')}>
                            {WEIGHT_GOAL_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {t(opt.label)}
                              </MenuItem>
                            ))}
                          </MuiCustomSelect>
                          {errors.weight_goal && (
                            <FormHelperText>{errors.weight_goal.message as string}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </div>
                )}
              </section>

              {/* Plan Config Section */}
              <section className="formSection">
                <Typography variant="h6" className="sectionTitle">
                  <Trans>Plan Configuration</Trans>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="title"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Plan Title')}
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.title}
                          helperText={errors.title?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" style={{ marginBottom: 4 }}>
                      <Trans>Number of Days</Trans>
                    </Typography>
                    <Controller
                      name="num_days"
                      control={control}
                      render={({ field }) => (
                        <div className="numDaysSelector">
                          {NUM_DAYS_OPTIONS.map((n) => (
                            <Button
                              key={n}
                              variant={field.value === n ? 'contained' : 'outlined'}
                              color={field.value === n ? 'primary' : 'default'}
                              onClick={() => field.onChange(n)}
                              className="numDaysBtn"
                            >
                              {n}
                            </Button>
                          ))}
                        </div>
                      )}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                  <Trans>Meal Slots</Trans>
                </Typography>
                <Controller
                  name="active_slots"
                  control={control}
                  render={({ field }) => {
                    const SLOT_LABELS: Record<keyof ActiveSlots, string> = {
                      breakfast: t('Breakfast'),
                      lunch: t('Lunch'),
                      dinner: t('Dinner'),
                      snacks: t('Snacks'),
                    };
                    return (
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {(['breakfast', 'lunch', 'dinner', 'snacks'] as const).map((slot) => (
                          <FormControlLabel
                            key={slot}
                            control={
                              <Switch
                                checked={field.value[slot]}
                                onChange={(e) => {
                                  field.onChange({ ...field.value, [slot]: e.target.checked });
                                }}
                                color="primary"
                              />
                            }
                            label={SLOT_LABELS[slot]}
                          />
                        ))}
                      </div>
                    );
                  }}
                />

                <Grid container spacing={2} style={{ marginTop: 8 }}>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="family_size"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Family Size')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          inputProps={{ min: 1, max: 12 }}
                          error={!!errors.family_size}
                          helperText={errors.family_size?.message as string}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                  <Trans>Macro Targets</Trans>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="target_calories"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Calories (kcal)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.target_calories}
                          helperText={errors.target_calories?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="target_protein_g"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Protein (g)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.target_protein_g}
                          helperText={errors.target_protein_g?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="target_carbs_g"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Carbs (g)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.target_carbs_g}
                          helperText={errors.target_carbs_g?.message as string}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Controller
                      name="target_fat_g"
                      control={control}
                      render={({ field }) => (
                        <MuiCustomTextField
                          {...field}
                          label={t('Fat (g)')}
                          type="number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          error={!!errors.target_fat_g}
                          helperText={errors.target_fat_g?.message as string}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {macroWarnings.length > 0 && (
                  <div className="macroWarnings">
                    {macroWarnings.map((warning, i) => (
                      <Typography key={i} variant="body2" className="macroWarning">
                        ⚠ {t(warning)}
                      </Typography>
                    ))}
                  </div>
                )}
              </section>

              {/* Dietary Preferences */}
              <section className="formSection">
                <Typography variant="h6" className="sectionTitle">
                  <Trans>Dietary Preferences</Trans>
                </Typography>

                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                  <Trans>Diet Labels</Trans>
                </Typography>
                <Controller
                  name="diet_labels"
                  control={control}
                  render={({ field }) => (
                    <div className="chipSelector">
                      {DIET_LABELS.map((label) => (
                        <Chip
                          key={label}
                          label={t(label)}
                          clickable
                          variant={field.value.includes(label) ? 'default' : 'outlined'}
                          color={field.value.includes(label) ? 'primary' : 'default'}
                          onClick={() => {
                            const newValue = field.value.includes(label)
                              ? field.value.filter((v) => v !== label)
                              : [...field.value, label];
                            field.onChange(newValue);
                          }}
                          className="dietChip"
                        />
                      ))}
                    </div>
                  )}
                />

                <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                  <Trans>Health Labels</Trans>
                </Typography>
                <Controller
                  name="health_labels"
                  control={control}
                  render={({ field }) => (
                    <div className="chipSelector">
                      {HEALTH_LABELS.map((label) => (
                        <Chip
                          key={label}
                          label={t(label)}
                          clickable
                          variant={field.value.includes(label) ? 'default' : 'outlined'}
                          color={field.value.includes(label) ? 'primary' : 'default'}
                          onClick={() => {
                            const newValue = field.value.includes(label)
                              ? field.value.filter((v) => v !== label)
                              : [...field.value, label];
                            field.onChange(newValue);
                          }}
                          className="dietChip"
                        />
                      ))}
                    </div>
                  )}
                />

                <div style={{ marginTop: 16 }}>
                  <Controller
                    name="exclusions"
                    control={control}
                    render={({ field }) => (
                      <MuiCustomTextField
                        {...field}
                        label={t('Exclusions (comma-separated)')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={t('e.g. shellfish, peanuts, cilantro')}
                      />
                    )}
                  />
                </div>
              </section>

              <section className="formSection">
                <Typography variant="h6" className="sectionTitle">
                  <Trans>Recent Foods Eaten</Trans>
                </Typography>
                <Controller
                  name="recent_meal_context_text"
                  control={control}
                  render={({ field }) => (
                    <MuiCustomTextField
                      {...field}
                      label={t('Recent food eaten (one food per line)')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      rows={15}
                    />
                  )}
                />
              </section>

              {/* Submit */}
              {submitError && (
                <Typography color="error" variant="body2" style={{ marginBottom: 16 }}>
                  {submitError}
                </Typography>
              )}
              <div className="formActions">
                <Button onClick={handleBack} variant="outlined">
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? t('Creating...') : t('Create Meal Plan')}
                </Button>
              </div>
            </form>
          </div>
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};
